import useMiniScent from "app/hooks/useMiniScent";
import { useEffect, useState, useCallback } from "react";
import {
  IDeviceUsageStats,
  IMiniScentCartridgeList,
  IMiniScentDevice,
  IShotHistoryList,
} from "../types";
import { useParams, useHistory } from "react-router-dom";
import store from "app/store/store";
import { useTranslation } from "_metronic/i18n/language";

const useMiniScentDetailModel = () => {
  const { serialNumber } = useParams<{ serialNumber: string }>();
  const history = useHistory();
  const translate = useTranslation();

  // Device states
  const [miniScentDevice, setMiniScentDevice] = useState<IMiniScentDevice>();
  const [deviceUsageStats, setDeviceUsageStats] = useState<IDeviceUsageStats>();
  const [isLoading, setIsLoading] = useState(true);

  // Cartridges pagination
  const [miniScentCartridgeList, setMiniScentCartridgeList] = useState<
    IMiniScentCartridgeList
  >();
  const [cartridgePage, setCartridgePage] = useState(0);
  const [cartridgeRowsPerPage, setCartridgeRowsPerPage] = useState(5);

  // Shot history pagination
  const [shotHistoryList, setShotHistoryList] = useState<IShotHistoryList>();
  const [shotHistoryPage, setShotHistoryPage] = useState(0);
  const [shotHistoryRowsPerPage, setShotHistoryRowsPerPage] = useState(5);

  const { auth } = store.getState();
  const {
    getStatusByDevice,
    listAllShootingHistory,
    listAllCatrigesByUser,
    searchDeviceBySerialNumber,
  } = useMiniScent();

  const getData = useCallback(async () => {
    if (auth.user && auth.user.id && serialNumber) {
      setIsLoading(true);
      try {
        const [
          newMiniScentDevice,
          statusDevice,
          catriges,
          shootingHistory,
        ] = await Promise.all([
          searchDeviceBySerialNumber(serialNumber),
          getStatusByDevice(serialNumber),
          listAllCatrigesByUser(auth.user.id as number),
          listAllShootingHistory(auth.user.id as number),
        ]);

        newMiniScentDevice && setMiniScentDevice(newMiniScentDevice);
        statusDevice && setDeviceUsageStats(statusDevice);
        catriges && setMiniScentCartridgeList(catriges);
        shootingHistory && setShotHistoryList(shootingHistory);
      } finally {
        setIsLoading(false);
      }
    }
  }, [serialNumber, auth.user]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleGoBack = () => {
    history.push("/admin/miniscent/devices");
  };

  const handleRefresh = () => {
    getData();
  };

  // Cartridge pagination handlers
  const handleCartridgePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setCartridgePage(newPage);
  };

  const handleCartridgeRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setCartridgeRowsPerPage(parseInt(event.target.value, 10));
    setCartridgePage(0);
  };

  // Shot history pagination handlers
  const handleShotHistoryPageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setShotHistoryPage(newPage);
  };

  const handleShotHistoryRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setShotHistoryRowsPerPage(parseInt(event.target.value, 10));
    setShotHistoryPage(0);
  };

  // Paginated data
  const paginatedCartridges = miniScentCartridgeList?.data.slice(
    cartridgePage * cartridgeRowsPerPage,
    cartridgePage * cartridgeRowsPerPage + cartridgeRowsPerPage,
  );

  const paginatedShotHistory = shotHistoryList?.data.slice(
    shotHistoryPage * shotHistoryRowsPerPage,
    shotHistoryPage * shotHistoryRowsPerPage + shotHistoryRowsPerPage,
  );

  return {
    // Data
    miniScentDevice,
    deviceUsageStats,
    miniScentCartridgeList,
    shotHistoryList,
    paginatedCartridges,
    paginatedShotHistory,
    isLoading,
    translate,

    // Cartridge pagination
    cartridgePage,
    cartridgeRowsPerPage,
    handleCartridgePageChange,
    handleCartridgeRowsPerPageChange,

    // Shot history pagination
    shotHistoryPage,
    shotHistoryRowsPerPage,
    handleShotHistoryPageChange,
    handleShotHistoryRowsPerPageChange,

    // Actions
    handleGoBack,
    handleRefresh,
  };
};

export default useMiniScentDetailModel;
